import { useState } from "react";
import type { Instrument, Operation, Position } from "../../sdk";
import { Button, CoinMark } from "../../components/ui";
import { useTickFlash } from "../../components/game";
import { useAccount } from "../../state/account";
import { cancelIntent, closePositions } from "../../state/actions";
import { useInstruments, useMark } from "../../state/market";
import { useValuation, useValuations } from "../../state/valuation";
import { big, pnl, tone, usd } from "../../lib/num";
import { ago } from "../../lib/time";
import { useNow } from "../bankroll/shared";
import { useDesk } from "../../lib/media";
import { BankrollChart, layerAlpha, type Mode } from "./BankrollChart";

function useClosing(): Set<string> {
  const operations = useAccount()?.operations ?? [];
  return new Set(operations.filter((op) => op.action === "closePositions").flatMap((op) => op.positionIds));
}

/** Where the mark sits between the bust (0) and the same distance the call's way (1); 0.5 at entry. */
function swing(position: Position, markRaw: string | undefined): number | null {
  if (!markRaw) return null;
  const entry = big(position.entryPrice);
  const bust = big(position.bustPrice);
  const mark = big(markRaw);
  const distance = position.isLong ? entry - bust : bust - entry;
  if (distance <= 0n) return null;
  const moved = position.isLong ? mark - entry : entry - mark;
  const bp = Number((moved * 10_000n) / distance);
  return Math.max(0, Math.min(1, 0.5 + bp / 20_000));
}

function Title({ symbol, isLong, margin, leverage }: { symbol: string; isLong: boolean; margin: string; leverage: number }) {
  return (
    <span className="ride-title">
      <CoinMark symbol={symbol || "?"} size={20} />
      <span className={isLong ? "up" : "down"}>{isLong ? "▲ Up" : "▼ Down"}</span>
      <span className="ride-symbol">{symbol}</span>
      <span className="ride-terms num">
        {usd(margin)} × {leverage}
      </span>
    </span>
  );
}

function Ride({
  position,
  instrument,
  closing,
  busy,
  layer,
  onCashOut,
}: {
  position: Position;
  instrument: Instrument | undefined;
  closing: boolean;
  busy: boolean;
  /** Its layer on the bankroll chart, bottom up, when it has one of its own. */
  layer: number | null;
  onCashOut: () => void;
}) {
  const valuation = useValuation(position.positionId);
  const mark = useMark(position.instrumentId);
  const net = valuation ? big(valuation.payoutPnl) : null;
  const back = valuation ? big(valuation.payout) : null;
  const flash = useTickFlash<HTMLSpanElement>(net);
  const at = swing(position, mark?.raw);
  const busting = valuation?.liquidated ?? false;
  const risk = valuation?.riskBp ?? 0;
  return (
    <article
      className={`ride ride-${net === null ? "flat" : tone(net)} ${risk >= 8000 ? "is-danger" : ""} ${busting ? "is-busting" : ""} ${layer === null ? "" : "has-swatch"}`}
      style={layer === null ? undefined : { ["--swatch-alpha" as string]: String(layerAlpha(layer) + 0.3) }}
    >
      <div className="ride-top">
        <Title symbol={instrument?.symbol ?? ""} isLong={position.isLong} margin={position.margin} leverage={position.leverage} />
        <span className="ride-age num">{ago(position.openedAtMs)}</span>
      </div>
      <div className="ride-mid">
        <span ref={flash} className={`ride-pnl num ${net === null ? "" : tone(net)}`}>
          {net === null ? "—" : pnl(net)}
        </span>
        <Button tone="primary" size="md" className="ride-cash" busy={busy} disabled={closing || busting || back === null} onClick={onCashOut}>
          {busting ? "Busting" : closing ? "Cashing out" : back === null ? "Cash out" : `Cash out ${usd(back)}`}
        </Button>
      </div>
      <div className="ride-gauge" role="img" aria-label={at === null ? "Waiting for the price" : `${Math.round(risk / 100)}% of the way to bust`}>
        <span className="ride-gauge-track">
          <span className="ride-gauge-mid" />
          {at !== null && (
            <span
              className={`ride-gauge-fill ${at >= 0.5 ? "is-up" : "is-down"}`}
              style={at >= 0.5 ? { left: "50%", width: `${(at - 0.5) * 100}%` } : { left: `${at * 100}%`, width: `${(0.5 - at) * 100}%` }}
            />
          )}
          {at !== null && <span className="ride-gauge-knob" style={{ left: `${at * 100}%` }} />}
        </span>
      </div>
    </article>
  );
}

function Placing({ operation, instruments, now }: { operation: Operation; instruments: Instrument[]; now: number }) {
  const [busy, setBusy] = useState(false);
  const symbol = instruments.find((instrument) => instrument.id === operation.instrumentId)?.symbol ?? "";
  return (
    <article className="ride ride-placing" aria-busy>
      <div className="ride-top">
        <Title symbol={symbol} isLong={operation.isLong ?? true} margin={operation.amount ?? "0"} leverage={operation.leverage ?? 0} />
        <span className="ride-age num">{ago(operation.acceptedAtMs, now)}</span>
      </div>
      <div className="ride-mid">
        <span className="ride-placing-words">
          <span className="spinner" aria-hidden />
          Placing your call
        </span>
        <Button
          size="sm"
          tone="ghost"
          busy={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await cancelIntent(operation.intentId);
            } catch {
              // told by a toast
            } finally {
              setBusy(false);
            }
          }}
        >
          Cancel
        </Button>
      </div>
    </article>
  );
}

/** Your open calls, each with what cashing out pays now. */
export function Rides() {
  const account = useAccount();
  const valuations = useValuations();
  const instruments = useInstruments();
  const closing = useClosing();
  const now = useNow(1_000);
  const [busy, setBusy] = useState<Set<string>>(new Set());
  const [confirmAll, setConfirmAll] = useState(false);
  const [picked, setPicked] = useState<Mode | null>(null);
  const desk = useDesk();

  if (!account) return null;
  const positions = account.positions;
  const placing = account.operations.filter((op) => op.action === "openPosition");
  if (positions.length === 0 && placing.length === 0) return null;

  const cashOut = async (ids: string[], label: string) => {
    setBusy((held) => new Set([...held, ...ids]));
    try {
      await closePositions(ids, label);
    } catch {
      // told by a toast
    } finally {
      setBusy((held) => new Set([...held].filter((id) => !ids.includes(id))));
    }
  };

  const open = positions.map((position) => position.positionId).filter((id) => !closing.has(id));
  const mode: Mode = picked ?? (positions.length > (desk ? 4 : 2) ? "total" : "split");
  // each call's layer on the chart, oldest at the bottom
  const layers = new Map([...positions].sort((a, b) => a.openedAtMs - b.openedAtMs).map((position, index) => [position.positionId, index]));
  const cashing = new Set([...closing, ...busy]);
  const instrumentOf = (position: Position) => instruments.find((item) => item.id === position.instrumentId);
  const total = valuations ? big(valuations.openPnl) : null;

  return (
    <section className="rides" aria-labelledby="rides-title">
      <header className="rides-head">
        <h2 id="rides-title" className="rides-title">
          Your live calls
        </h2>
        {total !== null && positions.length > 0 && <span className={`num rides-total ${tone(total)}`}>{pnl(total)}</span>}
        <span className="rides-spacer" />
        {positions.length > 1 &&
          (confirmAll ? (
            <span className="rides-confirm">
              <Button
                size="sm"
                tone="primary"
                onClick={() => {
                  setConfirmAll(false);
                  void cashOut(open, `Cash out ${open.length}`);
                }}
              >
                Cash out {open.length}
              </Button>
              <Button size="sm" tone="ghost" onClick={() => setConfirmAll(false)}>
                Keep
              </Button>
            </span>
          ) : (
            <Button size="sm" tone="ghost" disabled={open.length === 0} onClick={() => setConfirmAll(true)}>
              Cash out all
            </Button>
          ))}
      </header>
      {positions.length > 0 && (
        <div className="rides-chart">
          {positions.length > 1 && (
            <div className="rides-mode" role="radiogroup" aria-label="Show calls">
              {(["split", "total"] as const).map((value) => (
                <button key={value} type="button" role="radio" aria-checked={mode === value} onClick={() => setPicked(value)}>
                  {value === "split" ? "Each call" : "Together"}
                </button>
              ))}
            </div>
          )}
          <BankrollChart
            mode={mode}
            closing={cashing}
            onCashOut={(ids) => void cashOut(ids, ids.length > 1 ? `Cash out ${ids.length}` : `Cash out ${instrumentOf(positions.find((position) => position.positionId === ids[0]) as Position)?.symbol ?? ""}`)}
          />
        </div>
      )}
      <div className="rides-list">
        {placing.map((operation) => (
          <Placing key={operation.intentId} operation={operation} instruments={instruments} now={now} />
        ))}
        {positions.map((position) => (
          <Ride
            key={position.positionId}
            position={position}
            instrument={instrumentOf(position)}
            closing={closing.has(position.positionId)}
            busy={busy.has(position.positionId)}
            layer={mode === "split" ? (layers.get(position.positionId) ?? null) : null}
            onCashOut={() => void cashOut([position.positionId], `Cash out ${instrumentOf(position)?.symbol ?? ""}`)}
          />
        ))}
      </div>
    </section>
  );
}
